import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaUpload, FaTimes, FaFileAlt, FaCloudUploadAlt } from 'react-icons/fa';


interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<any>;
  title?: string;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  description?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = "Upload Document",
  maxFileSize = 10,
  acceptedTypes = [],
  description = "Select a file or drag and drop it here"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    console.log(`File size: ${fileSizeMB.toFixed(2)}MB, Max allowed: ${maxFileSize}MB`);
    
    if (fileSizeMB > maxFileSize) {
      const errorMsg = `File "${file.name}" (${fileSizeMB.toFixed(2)}MB) is too large. Maximum size is ${maxFileSize}MB. Please choose a smaller file.`;
      console.error(errorMsg);
      return errorMsg;
    }

    // Check file type if specified
    if (acceptedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type.toLowerCase();
      
      const isValidType = acceptedTypes.some(type => 
        type.startsWith('.') ? 
          fileExtension === type.slice(1) : 
          mimeType.includes(type)
      );
      
      if (!isValidType) {
        return `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`;
      }
    }

    return null;
  };

  const handleFileUpload = async (file: File) => {
    console.log(`Attempting to upload file: ${file.name}, size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    
    // Clear previous validation error
    setValidationError(null);
    
    const validationErrorMsg = validateFile(file);
    if (validationErrorMsg) {
      console.error('File validation failed:', validationErrorMsg);
      setValidationError(validationErrorMsg);
      toast.error(validationErrorMsg, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 200);

      await onUpload(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        toast.success('Document uploaded successfully!');
        setValidationError(null);
        onClose();
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Failed to upload document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle specific error types
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('fetch')) {
        toast.error('File is too large. Please try a smaller file.');
      } else if (errorMessage.includes('405')) {
        toast.error('Upload not supported by API. Please contact support.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to upload documents.');
      } else if (errorMessage.includes('413') || errorMessage.includes('Content Too Large')) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(`File "${file.name}" (${fileSizeMB}MB) is too large. Maximum size is 10MB. Please try a smaller file.`);
      } else {
        toast.error(`Failed to upload document: ${errorMessage}`);
      }
      
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);



  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center"
      style={{ zIndex: 999999 }}
    >
      <button
        onClick={onClose}
        disabled={isUploading}
        className="fixed top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
        style={{ zIndex: 1000000 }}
      >
        <FaTimes className="w-6 h-6" />
      </button>
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Upload Area */}
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
              ${isDragOver 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
              }
              ${isUploading ? 'pointer-events-none opacity-75' : 'cursor-pointer'}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept={acceptedTypes.length > 0 ? acceptedTypes.join(',') : '*/*'}
              disabled={isUploading}
            />

            {isUploading ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <FaCloudUploadAlt className="w-12 h-12 text-blue-500 animate-pulse" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Uploading...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">{Math.round(uploadProgress)}%</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <FaUpload className={`w-12 h-12 ${isDragOver ? 'text-blue-500' : 'text-gray-400'} transition-colors`} />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    {isDragOver ? 'Drop file here' : description}
                  </p>
                  <p className="text-xs text-gray-500">
                    Maximum file size: {maxFileSize}MB
                    {acceptedTypes.length > 0 && (
                      <span className="block mt-1">
                        Accepted types: {acceptedTypes.join(', ')}
                      </span>
                    )}
                  </p>
                  
                  {/* Validation Error */}
                  {validationError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <p className="text-sm text-red-700 font-medium">{validationError}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setValidationError(null);
                onClose();
              }}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaFileAlt className="w-4 h-4 inline mr-2" />
              Choose File
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
